import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, Copy, Pencil, Share2, Download, Receipt, Trash2 } from "lucide-react";
import { toast } from "sonner";

import PrescriptionPreview from "@/components/PrescriptionPreview";
import ReferralPreview from "@/components/ReferralPreview";
import { ReferralDialog } from "@/pages/Referrals";

import { downloadRxAsPdf, buildRxFilename } from "@/lib/rxPdf";

export default function PrescriptionView() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();

  const returnTo = params.get("returnTo") || "";

  const [prescription, setPrescription] = useState(null);
  const [settings, setSettings] = useState(null);
  const [referral, setReferral] = useState(null);
  const [followUpDate, setFollowUpDate] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [refOpen, setRefOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const printRootRef = useRef(null);

  // --- Page picker -----------------------------------------------------
  // A single Rx can produce several sheets (Rx page(s), lab results, request
  // forms, referral). Doctors often want just one of them re-printed, so we
  // list the sheets and let them untick the ones to leave out of this run.
  const [sheets, setSheets] = useState([]);      // [{ label }]
  const [skipped, setSkipped] = useState([]);    // indexes to leave out

  const papersOf = () =>
    printRootRef.current
      ? Array.from(printRootRef.current.querySelectorAll(".rx-paper"))
      : [];

  useEffect(() => {
    if (!loaded) return;
    // Let the preview paint before measuring.
    const t = setTimeout(() => {
      setSheets(
        papersOf().map((el, i) => ({
          label: el.getAttribute("data-print-label") || `Page ${i + 1}`,
        }))
      );
      setSkipped([]);
    }, 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [loaded, prescription, referral]);

  // On-screen feedback: grey out the sheets that won't come out of the printer.
  useEffect(() => {
    papersOf().forEach((el, i) =>
      el.classList.toggle("rx-deselected", skipped.includes(i))
    );
    // eslint-disable-next-line
  }, [skipped, sheets]);

  const toggleSheet = (i) =>
    setSkipped((prev) =>
      prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]
    );

  const selectedCount = sheets.length - skipped.length;

  // Mark the de-selected sheets (and any now-empty wrapper / hard page break,
  // which would otherwise still emit a blank sheet) for this print run only.
  const applySkips = () => {
    const root = printRootRef.current;
    if (!root) return () => {};

    const papers = papersOf();
    papers.forEach((el, i) => el.classList.toggle("print-skip", skipped.includes(i)));

    const wrappers = Array.from(root.querySelectorAll(".rx-page, .referral-page"));
    wrappers.forEach((w) => {
      const kids = Array.from(w.querySelectorAll(".rx-paper"));
      const empty = kids.length > 0 && kids.every((k) => k.classList.contains("print-skip"));
      w.classList.toggle("print-skip", empty);
    });

    // The hard break before the referral is only needed if the referral prints.
    const refPage = root.querySelector(".referral-page");
    const refHidden = !refPage || refPage.classList.contains("print-skip");
    root.querySelectorAll(".page-break").forEach((b) =>
      b.classList.toggle("print-skip", refHidden)
    );

    return () => {
      root
        .querySelectorAll(".print-skip")
        .forEach((el) => el.classList.remove("print-skip"));
    };
  };

  const loadAll = async () => {
    const [p, s] = await Promise.all([
      api.get(`/prescriptions/${id}`),
      api.get("/print-settings"),
    ]);

    setPrescription(p.data);
    setSettings(s.data || {});

    // Follow-up on the printed Rx is tied to the Follow-up module: read the
    // patient's CURRENT scheduled next visit (not the frozen snapshot).
    if (p.data?.patient_id) {
      try {
        const pt = await api.get(`/patients/${p.data.patient_id}`);
        setFollowUpDate(pt.data?.next_visit_date || "");
      } catch {
        setFollowUpDate("");
      }
    }

    if (p.data?.referral_id) {
      try {
        const r = await api.get(`/referrals/${p.data.referral_id}`);
        setReferral(r.data);
      } catch {
        setReferral(null);
      }
    } else {
      setReferral(null);
    }

    setLoaded(true);
  };

  useEffect(() => {
    loadAll().catch(() => toast.error("Could not load prescription"));
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (loaded && params.get("print") === "1") {
      setTimeout(() => doPrint(), 400);
    }
    // eslint-disable-next-line
  }, [loaded]);

  const doPrint = async () => {
    if (sheets.length && selectedCount === 0) {
      toast.error("Select at least one page to print");
      return;
    }

    try {
      await api.post(`/prescriptions/${id}/mark-printed`);
    } catch {}

    const restore = applySkips();
    // afterprint is the reliable signal; the timer is a fallback for browsers
    // that never fire it, so the preview can't get stuck with pages hidden.
    let done = false;
    const cleanup = () => {
      if (done) return;
      done = true;
      restore();
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    setTimeout(cleanup, 5000);

    window.print();
  };

  const doDownloadPdf = async () => {
    if (!printRootRef.current) {
      toast.error("Preview not ready");
      return;
    }

    setDownloading(true);
    const t = toast.loading("Building PDF…");

    try {
      await downloadRxAsPdf({
        root: printRootRef.current,
        filename: buildRxFilename(prescription),
        onProgress: ({ current, total }) => {
          toast.loading(`Building PDF… page ${current} of ${total}`, { id: t });
        },
      });

      toast.success("PDF downloaded", { id: t });
    } catch (e) {
      toast.error(e?.message || "Could not generate PDF", { id: t });
    } finally {
      setDownloading(false);
    }
  };

  const deletePrescription = async () => {
    if (!prescription?.id) return;

    const ok = window.confirm(
      "Delete this prescription?\n\nThis will remove it from active records, but it will remain in the audit trail."
    );
    if (!ok) return;

    try {
      await api.delete(`/prescriptions/${id}`);
      toast.success("Prescription deleted");
      nav(returnTo || "/prescriptions");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete prescription");
    }
  };

  const duplicate = async () => {
    try {
      const { data } = await api.post(`/prescriptions/${id}/duplicate`);
      toast.success("Duplicated as new draft");
      nav(`/prescriptions/${data.id}/edit`);
    } catch {
      toast.error("Could not duplicate");
    }
  };

  const createBill = async () => {
    try {
      const { data } = await api.post(`/pos/from-prescription/${id}`);
      toast.success(`POS ${data.transaction_number} ready`);
      nav(`/pos/transactions/${data.id}`);
    } catch {
      toast.error("Could not create POS bill");
    }
  };

  if (!loaded) {
    return <div className="p-8 text-slate-500">Loading...</div>;
  }

  return (
    <div className="p-6 md:p-8" data-testid="prescription-preview-page">

      {/* ================= ACTION BAR ================= */}
      <div className="no-print flex items-center justify-between mb-4 max-w-5xl mx-auto flex-wrap gap-2">
        <Button asChild variant="ghost">
          <Link to={returnTo || "/prescriptions"}>
            <ArrowLeft size={14} className="mr-2" />
            Back
          </Link>
        </Button>

        <div className="flex gap-2 flex-wrap">

          <Button variant="outline" onClick={() => nav(`/prescriptions/${id}/edit`)}>
            <Pencil size={14} className="mr-2" /> Edit
          </Button>

          <Button
            variant="outline"
            onClick={deletePrescription}
            className="text-red-600 border-red-200 hover:bg-red-50"
          >
            <Trash2 size={14} className="mr-2" /> Delete
          </Button>

          <Button variant="outline" onClick={duplicate}>
            <Copy size={14} className="mr-2" /> Duplicate
          </Button>

          <Button variant="outline" onClick={createBill}>
            <Receipt size={14} className="mr-2" /> POS
          </Button>

          <Button variant="outline" onClick={() => setRefOpen(true)}>
            <Share2 size={14} className="mr-2" />
            {referral ? "Update Referral" : "Create Referral"}
          </Button>

          <Button variant="outline" onClick={doDownloadPdf} disabled={downloading}>
            <Download size={14} className="mr-2" />
            {downloading ? "Building…" : "PDF"}
          </Button>

          <Button onClick={doPrint} className="bg-blue-600 hover:bg-blue-700">
            <Printer size={14} className="mr-2" />
            Print
          </Button>

        </div>
      </div>

      {/* ================= PAGE PICKER ================= */}
      {sheets.length > 1 && (
        <div
          className="no-print max-w-5xl mx-auto mb-4 p-3 bg-slate-50 border border-slate-200 rounded-md"
          data-testid="rx-page-picker"
        >
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="text-sm font-semibold text-slate-700">
              Pages to print
              <span className="ml-2 font-normal text-slate-500">
                {selectedCount} of {sheets.length} selected
              </span>
            </div>
            {skipped.length > 0 && (
              <button
                type="button"
                onClick={() => setSkipped([])}
                className="text-xs font-medium text-blue-600 hover:underline"
                data-testid="rx-page-picker-all"
              >
                Select all
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {sheets.map((sheet, i) => {
              const on = !skipped.includes(i);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => toggleSheet(i)}
                  aria-pressed={on}
                  className={
                    "flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition " +
                    (on
                      ? "bg-blue-600 border-blue-600 text-white"
                      : "bg-white border-slate-300 text-slate-500 hover:border-slate-400")
                  }
                  data-testid={`rx-page-toggle-${i}`}
                >
                  <span className="text-[11px] font-bold opacity-70">{i + 1}</span>
                  <span>{sheet.label}</span>
                </button>
              );
            })}
          </div>

          <div className="mt-2 text-xs text-slate-500">
            Untick a page to leave it out — handy for a second copy of just one sheet.
          </div>
        </div>
      )}

      {/* ================= PRINT BOUNDARY ================= */}
      <div ref={printRootRef} className="bg-white p-6 flex flex-col items-center print:block">

        {/* RX PAGE */}
        <div className="rx-page">
          <PrescriptionPreview
            prescription={prescription}
            settings={settings}
            followUpDate={followUpDate}
          />
        </div>

        {/* HARD PAGE BREAK */}
        {referral && <div className="page-break" />}

        {/* REFERRAL PAGE */}
        {referral && (
          <div className="referral-page">
            <ReferralPreview
              referral={referral}
              settings={settings}
            />
          </div>
        )}

      </div>

      {/* ================= BANNER ================= */}
      {referral && (
        <div className="no-print max-w-5xl mx-auto mb-3 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900">
          <b>Referral attached</b> — will print as a separate page after Rx.
        </div>
      )}

      {/* ================= REF DIALOG ================= */}
      <ReferralDialog
        open={refOpen}
        onOpenChange={setRefOpen}
        fixedPatientId={prescription?.patient_id || ""}
        fixedPrescriptionId={id}
        patientOptions={
          prescription?.patient_snapshot
            ? [{
                id: prescription.patient_id,
                full_name: prescription.patient_snapshot.full_name
              }]
            : []
        }
        initial={referral}
        onSaved={loadAll}
      />

    </div>
  );
}
